import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { TeamProvider } from '../contexts/TeamContext';
import { StyleProvider } from '../providers/StyleProvider';
import { PublicHome } from './components/PublicHomeNew'; // Use new component
import { PublicHome as LegacyPublicHome } from './components/PublicHome'; // Keep for fallback
import { ErrorBoundary } from './components/ErrorBoundary';
import { PublicForm } from './components/forms/PublicForm';
import { TestVideo } from './components/TestVideo';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

interface AppProps {
  pageSlug?: string | null;
  formSlug?: string | null;
  previewMode?: boolean;
  teamData?: {
    id: string;
    subdomain: string;
    name: string;
    teamLogoUrl?: string | null;
    style?: {
      backgroundColor?: string;
      headerColor?: string;
      backgroundPosition?: string;
      backgroundTiled?: boolean;
      backgroundImageUrl?: string | null;
      hasBackgroundImage?: boolean;
    };
    homePageOptions?: {
      tabAlignment?: 'left' | 'center' | 'right';
      navigationLayout?: 'vertical' | 'horizontal';
      navbarName?: string;
      customCss?: string;
      showHero?: boolean;
      heroTitle?: string;
      heroSubtitle?: string;
      heroCtaText?: string;
      heroCtaLink?: string;
      heroBackgroundType?: string;
      heroBackgroundColor?: string;
      heroGradientStartColor?: string;
      heroGradientEndColor?: string;
      heroGradientDirection?: string;
      heroFontFamily?: string;
      heroTextColor?: string;
      heroButtonBgColor?: string;
      heroButtonTextColor?: string;
      heroOverlayOpacity?: number;
      heroSecondaryCtaText?: string;
      heroSecondaryCtaLink?: string;
      heroSecondaryButtonBgColor?: string;
      heroSecondaryButtonTextColor?: string;
      heroSecondaryButtonBorderColor?: string;
      showHeroSecondaryCta?: boolean;
      heroDetailsBackgroundCard?: boolean;
      heroDetailsCardBackgroundColor?: string;
      heroDetailsCardOpacity?: number;
      overlayType?: string;
      overlayColor?: string;
      overlayGradientStart?: string;
      overlayGradientEnd?: string;
      overlayGradientDirection?: string;
    };
    socialMediaLinks?: {
      id: string;
      platform: 'linkedin' | 'facebook' | 'instagram' | 'twitter' | 'tiktok' | 'youtube' | 'website';
      url: string;
      username?: string;
      displayName?: string;
    }[];
  };
}


function ThemedApp({ teamData, pageSlug, formSlug, previewMode }: AppProps) {

  // If no team data, show legacy component (fallback)
  if (!teamData) {
    return (
      <BrowserRouter>
        <div className="min-h-screen w-full bg-transparent">
          <Routes>
            <Route path="/" element={<LegacyPublicHome />} />
            <Route path="/test_video" element={<TestVideo />} />
          </Routes>
        </div>
      </BrowserRouter>
    );
  }

  // With team data, use new component with providers
  return (
    <QueryClientProvider client={queryClient}>
      <TeamProvider teamData={{
        teamId: teamData.id,
        teamSlug: teamData.subdomain, // Use subdomain as slug
        teamSubdomain: teamData.subdomain,
        teamName: teamData.name,
        teamLogoUrl: teamData.teamLogoUrl || '', // Ensure logoUrl is defined
        teamStyle: teamData.style,
        homePageOptions: teamData.homePageOptions,
        socialMediaLinks: teamData.socialMediaLinks || []
      }}>
        <StyleProvider teamStyle={teamData.style}>
          <BrowserRouter>
            <div className="min-h-screen w-full bg-transparent">
              <Routes>
                <Route path="/" element={<PublicHome initialPageSlug={pageSlug} previewMode={previewMode} />} />
                <Route path="/pages/:pageSlug" element={<PublicHome previewMode={previewMode} />} />
                <Route path="/preview" element={<PublicHome previewMode={previewMode} />} />
                <Route path="/forms/:formSlug" element={<PublicForm />} />
                <Route path="/forms/:formSlug/new" element={<PublicForm />} />
                <Route path="/test_video" element={<TestVideo />} />
              </Routes>
            </div>
          </BrowserRouter>
        </StyleProvider>
      </TeamProvider>
    </QueryClientProvider>
  );
}

export function App({ teamData, pageSlug, formSlug, previewMode }: AppProps) {
  return (
    <ErrorBoundary>
      <ThemedApp teamData={teamData} pageSlug={pageSlug} formSlug={formSlug} previewMode={previewMode} />
    </ErrorBoundary>
  );
}